import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLoginModal } from '../components/LoginModal';
import { Ticket, ShoppingBag, Package, X, Check, Loader2, History, ChevronRight, Wallet } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PageHeader } from '../components/Layout';
import { EmptyState } from '../components/EmptyState';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { SEOHead } from '../components/SEOHead';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Shop = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openLogin } = useLoginModal();
  const { user, token, refreshUser } = useAuth();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [redemptionHistory, setRedemptionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showConnectDialog, setShowConnectDialog] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/shop/products`);
      setProducts(res.data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (!token) return;
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/shop/my-purchases`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setRedemptionHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleShowHistory = () => {
    if (!token) {
      openLogin();
      return;
    }
    setShowHistory(true);
    fetchHistory();
  };

  const handleProductClick = (product) => {
    if (!token) {
      openLogin();
      return;
    }
    setSelectedProduct(product);
    setUserInput('');
    setPurchaseSuccess(false);
    setShowPurchaseDialog(true);
  };

  const handlePurchase = async () => {
    if (!selectedProduct) return;
    
    // Validate user input if required
    if (selectedProduct.requires_user_input && !userInput.trim()) {
      toast.error(`Please enter your ${selectedProduct.user_input_label || 'information'}`);
      return;
    }
    
    setPurchasing(true);
    try {
      await axios.post(
        `${API}/shop/purchase`,
        { 
          product_id: selectedProduct.id,
          user_input: userInput || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPurchaseSuccess(true);
      toast.success('Product redeemed successfully!');
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const closePurchaseDialog = () => {
    setShowPurchaseDialog(false);
    setSelectedProduct(null);
    setPurchaseSuccess(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'general': return 'General';
      case 'balance': return 'Balance';
      case 'merchandise': return 'Merchandise';
      case 'voucher': return 'Voucher';
      default: return 'General';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'general': return 'bg-zinc-800 text-zinc-400';
      case 'balance': return 'bg-green-900/50 text-green-400';
      case 'merchandise': return 'bg-purple-900/50 text-purple-400';
      case 'voucher': return 'bg-blue-900/50 text-blue-400';
      default: return 'bg-zinc-800 text-zinc-400';
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <SEOHead page="shop" />
      <PageHeader title={t('shop.title')} />

      {/* Ticket Balance Card */}
      <div className="px-5 pt-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative p-4 rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)',
            border: '1px solid rgba(139, 92, 246, 0.3)'
          }}
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-2 right-2 w-24 h-24 rounded-full bg-violet-500 blur-3xl" />
            <div className="absolute bottom-2 left-2 w-16 h-16 rounded-full bg-purple-500 blur-2xl" />
          </div>
          
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Ticket className="w-6 h-6 text-[#FF8C00]" />
              </div>
              <div>
                <p className="text-zinc-400 text-xs uppercase tracking-wider">{t('shop.yourTickets', { defaultValue: 'Your Tickets' })}</p>
                <p className="text-2xl font-bold text-[#1A1206] font-['Chakra_Petch']">
                  {user?.tickets || 0}
                </p>
              </div>
            </div>
            <ShoppingBag className="w-8 h-8 text-violet-500/50" />
          </div>
        </motion.div>

        {/* History Button */}
        {token && (
          <div className="mt-3">
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              onClick={handleShowHistory}
              className="w-full flex items-center justify-center gap-2 p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:bg-zinc-800/50 transition-colors"
              data-testid="history-btn"
            >
              <History className="w-5 h-5 text-violet-400" />
              <span className="text-white text-sm">{t('shop.redemptionHistory')}</span>
            </motion.button>
          </div>
        )}
      </div>

      {/* Products Grid */}
      <div className="px-5 py-4">
        <h3 className="text-lg font-bold text-[#1A1206] uppercase tracking-wider font-['Chakra_Petch'] mb-4">
          {t('shop.availableProducts', { defaultValue: 'Available Products' })}
        </h3>

        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="aspect-square bg-zinc-900 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <EmptyState
            title={t('shop.noProducts')}
            subtitle={t('shop.collectTickets', { defaultValue: 'Collect Tickets from gacha!' })}
            size={150}
          />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3 pb-24">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: Math.min(index * 0.04, 0.4) }}
                whileHover={{ y: -4 }}
                onClick={() => handleProductClick(product)}
                className="group bg-gradient-to-b from-[#1a1030]/80 to-[#0e2a45]/60 border border-violet-500/20 rounded-2xl p-2 cursor-pointer hover:border-violet-400/70 hover:shadow-[0_0_22px_-6px_rgba(167,139,250,0.6)] transition-all active:scale-[0.98]"
                data-testid={`product-${product.id}`}
              >
                {/* Product Image */}
                <div className="aspect-square relative rounded-xl overflow-hidden">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Category Badge */}
                  <div className={`absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${getCategoryColor(product.category)}`}>
                    {getCategoryLabel(product.category)}
                  </div>
                  {/* Stock Badge */}
                  {product.stock !== null && (
                    <div className="absolute top-1.5 right-1.5 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px]">
                      {product.stock} {t('shop.left', { defaultValue: 'left' })}
                    </div>
                  )}
                </div>
                
                {/* Product Info */}
                <div className="px-1.5 pt-2 pb-1">
                  <h4 className="text-[#1A1206] font-medium text-xs sm:text-sm truncate mb-1.5">{product.name}</h4>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 bg-violet-500/15 border border-violet-500/30 rounded-full px-2 py-0.5">
                      <Ticket className="w-3.5 h-3.5 text-violet-300" />
                      <span className="text-violet-200 font-bold text-xs font-['Chakra_Petch']">{product.ticket_price}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-violet-400/60 group-hover:text-violet-300 transition-colors" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Purchase Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={closePurchaseDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm p-0 overflow-hidden">
          <AnimatePresence mode="wait">
            {purchaseSuccess ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="p-6 text-center"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-900/30 flex items-center justify-center">
                  <Check className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t('common.success')}!</h3>
                <p className="text-zinc-400 mb-6">{t('shop.redemptionSubmitted', { defaultValue: 'Your redemption request has been submitted. Please wait for admin approval.' })}</p>
                <Button
                  onClick={closePurchaseDialog}
                  className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl"
                >
                  {t('common.close')}
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {selectedProduct && (
                  <>
                    {/* Product Image */}
                    <div className="aspect-video relative">
                      <img
                        src={selectedProduct.image_url}
                        alt={selectedProduct.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                    </div>
                    
                    {/* Product Info */}
                    <div className="p-4 -mt-8 relative">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mb-2 ${getCategoryColor(selectedProduct.category)}`}>
                        {getCategoryLabel(selectedProduct.category)}
                      </span>
                      <h3 className="text-xl font-bold text-white">{selectedProduct.name}</h3>
                      <p className="text-zinc-400 text-sm mt-1">{selectedProduct.description}</p>
                      
                      {/* Price */}
                      <div className="flex items-center justify-between mt-4 p-3 bg-zinc-800/50 rounded-xl">
                        <span className="text-zinc-400">{t('common.price')}</span>
                        <div className="flex items-center gap-2">
                          <Ticket className="w-5 h-5 text-violet-400" />
                          <span className="text-violet-400 font-bold text-lg font-['Chakra_Petch']">{selectedProduct.ticket_price}</span>
                        </div>
                      </div>
                      
                      {/* User Balance */}
                      <div className="flex items-center justify-between mt-2 p-3 bg-zinc-800/50 rounded-xl">
                        <span className="text-zinc-400">{t('shop.yourBalance', { defaultValue: 'Your Balance' })}</span>
                        <div className="flex items-center gap-2">
                          <Ticket className="w-5 h-5 text-violet-400" />
                          <span className="text-violet-400 font-bold text-lg font-['Chakra_Petch']">{user?.tickets || 0}</span>
                        </div>
                      </div>
                      
                      {/* User Input Field */}
                      {selectedProduct.requires_user_input && (
                        <div className="mt-4">
                          <label className="block text-zinc-400 text-sm mb-2">
                            {selectedProduct.user_input_label || t('shop.additionalInfo', { defaultValue: 'Additional Information' })}
                            <span className="text-red-400 ml-1">*</span>
                          </label>
                          <Input
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            placeholder={selectedProduct.user_input_placeholder || t('shop.enterInfo', { defaultValue: 'Enter your information' })}
                            className="bg-zinc-800 border-zinc-700 text-white"
                          />
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="flex gap-3 mt-6">
                        <Button
                          onClick={closePurchaseDialog}
                          variant="outline"
                          className="flex-1 h-12 border-zinc-700 text-white rounded-xl"
                        >
                          {t('common.cancel')}
                        </Button>
                        <Button
                          onClick={handlePurchase}
                          disabled={purchasing || (user?.tickets || 0) < selectedProduct.ticket_price}
                          className="flex-1 h-12 bg-gradient-to-r from-amber-600 to-blue-600 hover:from-amber-700 hover:to-blue-700 text-white font-bold rounded-xl disabled:opacity-50"
                        >
                          {purchasing ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (user?.tickets || 0) < selectedProduct.ticket_price ? (
                            t('shop.insufficientTickets')
                          ) : (
                            t('shop.redeem')
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md h-[70vh] flex flex-col p-0">
          <DialogHeader className="p-4 border-b border-zinc-800 flex-shrink-0">
            <DialogTitle className="text-white font-['Chakra_Petch']">{t('shop.redemptionHistory')}</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto p-4">
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
              </div>
            ) : redemptionHistory.length === 0 ? (
              <EmptyState
                title={t('shop.noRedemptions')}
                subtitle={t('shop.exchangeTickets', { defaultValue: 'Exchange Tickets for products!' })}
                size={120}
              />
            ) : (
              <div className="space-y-3">
                {redemptionHistory.map((item) => (
                  <div key={item.id} className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      <img
                        src={item.product_image}
                        alt={item.product_name}
                        className="w-14 h-14 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium text-sm truncate">{item.product_name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Ticket className="w-3 h-3 text-violet-400" />
                          <span className="text-violet-400 text-xs font-medium">{item.ticket_spent} {t('wallet.tickets')}</span>
                        </div>
                        <p className="text-zinc-500 text-xs mt-1">{formatDate(item.created_at)}</p>
                        <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium capitalize
                          ${item.status === 'approved' ? 'bg-green-900/50 text-green-400' :
                            item.status === 'rejected' ? 'bg-red-900/50 text-red-400' :
                            item.status === 'completed' ? 'bg-blue-900/50 text-blue-400' :
                            'bg-violet-900/50 text-violet-400'}`}
                        >
                          {t(`shop.${item.status}`, { defaultValue: item.status })}
                        </span>
                      </div>
                    </div>
                    {item.admin_note && (
                      <p className="text-zinc-500 text-xs mt-2 pl-[68px]">{t('shop.note', { defaultValue: 'Note' })}: {item.admin_note}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Connect Wallet Dialog (shown when not logged in and trying to buy/sell) */}
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent className="bg-white border border-[#FFC300]/30 max-w-sm p-8 text-center" data-testid="shop-connect-wallet-dialog">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FFC300] to-[#FFC300] flex items-center justify-center mx-auto mb-5 shadow-[0_10px_30px_-6px_rgba(0,157,174,0.7)]">
            <Wallet className="w-10 h-10 text-[#FF8C00]" />
          </div>
          <h2 className="text-2xl font-black text-[#1A1206] mb-2 font-['Unbounded']">Connect Your Wallet</h2>
          <p className="text-[#6E6452] mb-7 leading-relaxed">
            Sorry, you must Connect your Wallet to buy or sell cards.
          </p>
          <Button
            onClick={() => { setShowConnectDialog(false); openLogin(); }}
            className="w-full h-14 bg-[#FFFDF7] hover:bg-[#FFC300] hover:text-[#06212E] text-white font-bold text-lg rounded-xl transition-all flex items-center justify-center gap-2"
            data-testid="shop-connect-wallet-btn"
          >
            <Wallet className="w-5 h-5" />
            Connect Wallet
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
